import * as THREE from "three";
import {
  FillPattern,
  SymbolType,
} from "../../../types/floorplannerTypes";
import { CatalogSurfaceReference } from "../products/product3DTypes";
import {
  getBuiltInFurnitureMaterial,
  isGlassBuiltInFurnitureMaterial,
  type BuiltInFurnitureMaterial,
} from "./builtInFurnitureMaterials";
import { resolveCatalogSurfaceRefForRender } from "./builtInMaterialSurfaceMappings";
import { resolveMaterial } from "./materialResolver";
import type { FloorPlan3DMaterialSlot } from "./materialResolver";

export type PrimitiveSymbolPartMaterialParams = {
  color: THREE.Color;
  roughness?: number;
  metalness?: number;
  builtIn?: BuiltInFurnitureMaterial;
  isGlass: boolean;
};

const DEFAULT_PRIMITIVE_SYMBOL_FILL_COLOR = 0xd8d0c4;
const DEFAULT_SYMBOL_FILL_COLOR = 0xffffff;

export function shouldUseSymbolFillColorIn3D(symbol: SymbolType): boolean {
  return (
    symbol.fillColor !== undefined &&
    (symbol.threeD?.fillColorOverride === true ||
      symbol.fillColor !== DEFAULT_SYMBOL_FILL_COLOR)
  );
}

export function hasAssignedSymbolMaterial(symbol: SymbolType): boolean {
  if (symbol.threeD?.surfaceRef?.id) return true;

  return Object.values(symbol.threeD?.materialAssignments ?? {}).some(
    (assignment) =>
      Boolean(assignment?.materialId || assignment?.surfaceRef?.id),
  );
}

export function hasAssignedSlotMaterial(
  symbol: SymbolType,
  slot: FloorPlan3DMaterialSlot,
): boolean {
  const assignment = symbol.threeD?.materialAssignments?.[slot];
  return Boolean(assignment?.materialId || assignment?.surfaceRef?.id);
}

export function resolvePrimitiveSymbolPartSurfaceRef(
  symbol: SymbolType,
  slot: FloorPlan3DMaterialSlot,
): CatalogSurfaceReference | undefined {
  const assignment = symbol.threeD?.materialAssignments?.[slot];
  const surfaceRef = assignment?.surfaceRef;
  if (!surfaceRef) return undefined;

  // Lean + legacy surfaceRefs: resolve albedo / sheen from the live catalog.
  return resolveCatalogSurfaceRefForRender(
    surfaceRef,
    assignment?.materialId ?? surfaceRef.id,
  );
}

export function resolveSymbolPattern(
  symbol: SymbolType,
): FillPattern | undefined {
  return hasAssignedSymbolMaterial(symbol) ? undefined : symbol.fillPattern;
}

export function resolvePrimitiveSymbolFillColor(symbol: SymbolType): THREE.Color {
  if (symbol.fillColor !== undefined) {
    return new THREE.Color(symbol.fillColor);
  }

  return resolveMaterial("wall", undefined, {
    defaultColor: DEFAULT_PRIMITIVE_SYMBOL_FILL_COLOR,
  }).color;
}

export function resolvePrimitiveSymbolPartColor(
  symbol: SymbolType,
  slot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  /**
   * Set for hardware that should keep its material colour when the user tints
   * the symbol - bed legs stay grey while the bedding follows the fill colour.
   */
  ignoreSymbolFillColor = false,
): THREE.Color {
  return resolvePrimitiveSymbolPartMaterial(
    symbol,
    slot,
    useFillColor,
    ignoreSymbolFillColor,
  ).color;
}

/**
 * Resolves color + PBR for a primitive part, including built-in furniture
 * presets assigned by materialId.
 */
export function resolvePrimitiveSymbolPartMaterial(
  symbol: SymbolType,
  slot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  ignoreSymbolFillColor = false,
): PrimitiveSymbolPartMaterialParams {
  if (useFillColor) {
    return {
      color: resolvePrimitiveSymbolFillColor(symbol),
      isGlass: false,
    };
  }

  if (hasAssignedSlotMaterial(symbol, slot)) {
    const assignment = symbol.threeD?.materialAssignments?.[slot];
    const surfaceRef = assignment?.surfaceRef;
    const builtIn = getBuiltInFurnitureMaterial(
      assignment?.materialId ?? surfaceRef?.id,
    );
    const resolved = resolveMaterial(
      slot,
      symbol.threeD ? { threeD: symbol.threeD } : undefined,
    );
    return {
      color:
        surfaceRef?.color !== undefined
          ? new THREE.Color(surfaceRef.color)
          : resolved.color,
      roughness: surfaceRef?.roughness ?? resolved.roughness ?? builtIn?.roughness,
      metalness: surfaceRef?.metalness ?? resolved.metalness ?? builtIn?.metalness,
      builtIn,
      isGlass: isGlassBuiltInFurnitureMaterial(builtIn),
    };
  }

  if (!ignoreSymbolFillColor && shouldUseSymbolFillColorIn3D(symbol)) {
    return {
      color: resolvePrimitiveSymbolFillColor(symbol),
      isGlass: false,
    };
  }

  const resolved = resolveMaterial(
    slot,
    symbol.threeD ? { threeD: symbol.threeD } : undefined,
  );
  return {
    color: resolved.color,
    roughness: resolved.roughness,
    metalness: resolved.metalness,
    isGlass: false,
  };
}
